import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ArrowRight, LogIn, MailCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LoginFormData, loginSchema } from "@/model/schemas";
import { authService } from "@/services/authService";
import { authErrorMessage } from "@/util/authError";
import { zodFormResolver } from "@/util/form";
import AuthCard from "./auth/AuthCard";
import EmailSentPanel from "./auth/EmailSentPanel";
import { ctaVariants } from "./site/CtaLink";
import { TextField as BaseTextField } from "./site/FormFields";
import TransitionLink from "./TransitionLink";

const TextField = BaseTextField<LoginFormData>;

const Login = () => {
  const { isAuthenticated, authHydrated, refreshAuthStatus } = useAuth();
  const navigate = useNavigate();
  const [setupEmail, setSetupEmail] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const form = useForm<LoginFormData>({
    resolver: zodFormResolver<LoginFormData>(loginSchema),
    mode: "onTouched",
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (data: LoginFormData) => {
    setErrorMessage(null);
    const email = data.email.trim();
    try {
      const result = await authService.login(email, data.password);
      if (result === "password-setup-required") {
        setSetupEmail(email);
        return;
      }
      await refreshAuthStatus();
      navigate("/dashboard");
    } catch (error) {
      setErrorMessage(authErrorMessage(error));
    }
  };

  useEffect(() => {
    if (authHydrated && isAuthenticated) {
      navigate("/dashboard");
    }
  }, [authHydrated, isAuthenticated]);

  if (!authHydrated) {
    return null;
  }

  if (setupEmail) {
    return (
      <AuthCard icon={MailCheck} title="Check your email" subtitle="We've upgraded how you sign in to OnHome.">
        <EmailSentPanel
          email={setupEmail}
          message="To keep your account secure, please set a new password. We just sent a link to"
          onResend={() => authService.forgotPassword(setupEmail)}
        />
        <button
          type="button"
          onClick={() => setSetupEmail(null)}
          className="mt-6 cursor-pointer text-[15px] font-semibold text-brand-ink-strong transition-colors hover:text-navy"
        >
          Back to sign in
        </button>
      </AuthCard>
    );
  }

  return (
    <AuthCard icon={LogIn} title="Welcome back" subtitle="Sign in to your OnHome account.">
      <FormProvider {...form}>
        <form noValidate onSubmit={form.handleSubmit(onSubmit)} className="grid gap-6">
          <TextField name="email" label="Email" type="email" autoComplete="email" placeholder="you@example.com" />
          <div>
            <TextField name="password" label="Password" type="password" autoComplete="current-password" />
            <div className="mt-2 text-right">
              <TransitionLink
                to="/forgot-password"
                className="text-sm font-semibold text-brand-ink-strong transition-colors hover:text-navy"
              >
                Forgot password?
              </TransitionLink>
            </div>
          </div>
          {errorMessage && (
            <p role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
              {errorMessage}
            </p>
          )}
          <button
            type="submit"
            disabled={form.formState.isSubmitting}
            className={ctaVariants({ className: "w-full" })}
          >
            {form.formState.isSubmitting ? "Signing in…" : "Sign in"}
            <ArrowRight className="size-4" aria-hidden />
          </button>
        </form>
      </FormProvider>
      <p className="mt-6 text-center text-[15px] text-slate-600">
        Don't have an account?{" "}
        <TransitionLink to="/register" className="font-semibold text-brand-ink-strong transition-colors hover:text-navy">
          Create one
        </TransitionLink>
      </p>
    </AuthCard>
  );
};

export default Login;
